import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type { CoachSession, SessionFormat, SessionInput } from '@/types/coach';

const FORMAT_OPTIONS: { value: SessionFormat; label: string }[] = [
  { value: 'in_person', label: 'Persönlich' },
  { value: 'phone', label: 'Telefon' },
  { value: 'video', label: 'Video-Call' },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  initial?: CoachSession | null;
  onSaved: (session: CoachSession) => void;
}

function toDateInput(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function SessionFormModal({ open, onOpenChange, clientId, initial, onSaved }: Props) {
  const [date, setDate] = useState('');
  const [format, setFormat] = useState<SessionFormat>('video');
  const [duration, setDuration] = useState<string>('60');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setDate(toDateInput(initial.date));
      setFormat(initial.format);
      setDuration(initial.durationMinutes !== null ? String(initial.durationMinutes) : '');
    } else {
      const today = new Date().toISOString().slice(0, 10);
      setDate(today);
      setFormat('video');
      setDuration('60');
    }
  }, [open, initial]);

  async function handleSave() {
    if (!date) {
      toast.error('Datum ist erforderlich.');
      return;
    }
    const dur = duration.trim() ? parseInt(duration, 10) : undefined;
    if (dur !== undefined && (Number.isNaN(dur) || dur < 0 || dur > 480)) {
      toast.error('Dauer muss zwischen 0 und 480 Minuten liegen.');
      return;
    }

    const payload: SessionInput = {
      date: new Date(`${date}T00:00:00`).toISOString(),
      format,
      ...(dur !== undefined ? { durationMinutes: dur } : {}),
    };

    setSaving(true);
    try {
      const saved = initial
        ? await api.coach.sessions.update(initial.id, {
            date: payload.date,
            format: payload.format,
            durationMinutes: dur ?? null,
          })
        : await api.coach.sessions.create(clientId, payload);
      onSaved(saved);
      onOpenChange(false);
      toast.success(initial ? 'Session aktualisiert.' : 'Session angelegt.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{initial ? 'Session bearbeiten' : 'Neue Session'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <label className="block">
            <span className="text-sm font-medium">Datum</span>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium">Format</span>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value as SessionFormat)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {FORMAT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-sm font-medium">Dauer (Minuten, optional)</span>
            <Input
              type="number"
              min={0}
              max={480}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="mt-1"
            />
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Speichere…' : 'Speichern'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
